import { useCallback, useMemo, useRef, useState } from 'react'
import { useService } from '../../../hooks/use-service'
import { NotificationService } from '../../../services'
import { INotificationFieldSuggestions, IPageAnalysisError } from '../../../interfaces/notification-field-suggestions'
import { DomainDto } from '../../../dtos/domain'
import { MediaUploadOwner, useCompositeImageLoader } from './use-composite-image-loader'
import { pause } from '../../../_utils/utils'
import { sanitizeKeywords } from '../../../_utils/keywords'

/**
 * Result from a page analysis request.
 * Contains either suggestions (on success) or an error (on failure), never both.
 */
export type PageAnalyzerResult = {
    suggestions: INotificationFieldSuggestions | null
    error: IPageAnalysisError | null
}

type PageAnalysisDomain = Pick<DomainDto, 'id'>

/**
 * Where one analysis request runs. The domain decides the API route, the page-analysis config the
 * analyzer loads and the field settings the API filters the response with; the media owner decides
 * where the processed suggested image is stored (the hook's own domain unless told otherwise).
 */
export type PageAnalysisTarget = {
    domain: Pick<DomainDto, 'id'>
    mediaOwner?: MediaUploadOwner
}

/**
 * API surface for the page analyzer hook.
 * Provides an analyze function that returns structured results with error handling.
 */
export type PageAnalyzerApi = {
    readonly isAnalyzing: boolean
    /**
     * Analyze `url` for `target`. Without a target the request runs under the domain the hook was
     * created with; a multi-domain form passes the domain that stands in for the send.
     */
    analyze(url: string, target?: PageAnalysisTarget): Promise<PageAnalyzerResult>
}

const EMPTY_RESULT: PageAnalyzerResult = { suggestions: null, error: null }

/**
 * A request that got no answer from the API (network failure, server error, permission denial)
 * is a failure the user should see, not a page with nothing to suggest.
 */
export const REQUEST_FAILED_ERROR: IPageAnalysisError = {
    message: 'The landing page could not be analyzed right now. Please try again.',
    statusCode: null,
    referenceUrl: null,
    details: null,
}

const buildResponse = (data: INotificationFieldSuggestions | null): INotificationFieldSuggestions | null => {
    if (!data || Object.values(data).every((v) => (Array.isArray(v) ? !v.length : v === null))) return null
    return data
}

/** Replay key for the most recent request: the same URL analyzed under another domain is a new request. */
const buildRequestKey = (domainId: number, url: string): string => `${domainId}|${url}`

export const usePageAnalyzer = (boundDomain: PageAnalysisDomain): PageAnalyzerApi => {
    const notifSvc = useService(NotificationService)

    const [isAnalyzing, setIsAnalyzing] = useState(false)
    // The ref is what the async callback reads (state would be a stale closure); the state drives
    // re-renders for consumers of `isAnalyzing`.
    const isAnalyzingRef = useRef(false)
    const setAnalyzing = useCallback((value: boolean) => {
        isAnalyzingRef.current = value
        setIsAnalyzing(value)
    }, [])

    // Only an answer from the API is replayed.
    const lastRequestKeyRef = useRef<string | null>(null)
    const resultsRef = useRef<PageAnalyzerResult>(EMPTY_RESULT)

    const loadCompositeImage = useCompositeImageLoader(boundDomain)

    const analyze = useCallback(
        async (url: string, target?: PageAnalysisTarget): Promise<PageAnalyzerResult> => {
            if (isAnalyzingRef.current) return EMPTY_RESULT

            const domainId = target?.domain.id ?? boundDomain.id
            const mediaOwner = target?.mediaOwner
            const requestKey = buildRequestKey(domainId, url)

            try {
                setAnalyzing(true)

                if (lastRequestKeyRef.current === requestKey) {
                    await pause(500)
                    return {
                        suggestions: buildResponse(resultsRef.current.suggestions),
                        error: resultsRef.current.error,
                    }
                }

                const res = await notifSvc.analyzeLandingUrl(domainId, url, {
                    showLoadingScreen: false,
                    cancellationKey: 'nfs.analyze',
                })

                // A superseded request is silent; any other missing answer is reported, and neither
                // is recorded, so the next click tries again.
                if (res.cancelled) return EMPTY_RESULT
                if (!res.ok) return { suggestions: null, error: REQUEST_FAILED_ERROR }

                const analysisError = res.data?.error ?? null
                if (analysisError) {
                    lastRequestKeyRef.current = requestKey
                    resultsRef.current = { suggestions: null, error: analysisError }
                    return resultsRef.current
                }

                let imageUrl = res.data.imageUrl
                let compositeImage = res.data.compositeImage ?? null
                if (imageUrl) {
                    const loaded = await loadCompositeImage(imageUrl, mediaOwner)
                    if (loaded) {
                        imageUrl = loaded.url
                        compositeImage = loaded
                    }
                }

                const data: INotificationFieldSuggestions = {
                    ...res.data,
                    keywords: sanitizeKeywords(res.data.keywords),
                    imageUrl,
                    compositeImage,
                }

                lastRequestKeyRef.current = requestKey
                resultsRef.current = { suggestions: buildResponse(data), error: null }

                return resultsRef.current
            } finally {
                setAnalyzing(false)
            }
        },
        [boundDomain.id, loadCompositeImage, notifSvc, setAnalyzing],
    )

    return useMemo(
        () => ({
            analyze,
            isAnalyzing,
        }),
        [analyze, isAnalyzing],
    )
}
